import compact from 'lodash/compact';
import isNil from 'lodash/isNil';
import pick from 'lodash/pick';
import uniqBy from 'lodash/uniqBy';

import { isManuallyHandledCommunicationStatus, isSentCommunicationStatus } from '../communicationUtils';
import { isNilOrEmpty } from '../objectUtils';
import { getSettingValueForBoolean } from '../settingutils';
import {
  type Charge,
  type ChargeStatement,
  type Communication,
  CommunicationStatus,
  CommunicationType,
  type File,
  type Setting,
  SettingType,
} from '../types';

const STATEMENT_COMMUNICATION_TYPES: CommunicationType[] = [
  CommunicationType.LEASE_TENANT_STATEMENT,
  CommunicationType.OWNER_STATEMENT,
];

const PENDING_COMMUNICATION_STATUSES: CommunicationStatus[] = [
  CommunicationStatus.DRAFT,
  CommunicationStatus.SCHEDULED,
  CommunicationStatus.SNOOZED,
  CommunicationStatus.TO_SEND,
];

export enum StatementSupportingDocumentsPreference {
  WORKSPACE_DEFAULT = 'WORKSPACE_DEFAULT',
  ALWAYS = 'ALWAYS',
  NEVER = 'NEVER',
}

/** Where the communication history reads the supporting documents of a statement communication from. */
export enum SupportingDocumentsSource {
  RECORDED_AT_SEND = 'RECORDED_AT_SEND',
  STATEMENT = 'STATEMENT',
  NONE = 'NONE',
}

const COMMUNICATION_COPY_FIELDS = [
  'addressId',
  'body',
  'buildingId',
  'channel',
  'communicationTemplateId',
  'concernedContactIds',
  'contactId',
  'contactIds',
  'hideLogo',
  'invoiceId',
  'leaseId',
  'leasePriceHistoryId',
  'readers',
  'recipient',
  'recipientContactIdsInBcc',
  'recipientContactIdsInCc',
  'recipientInBcc',
  'recipientInCc',
  'sender',
  'signatureDocumentId',
  'statementId',
  'subject',
  'type',
  'unitId',
] as const satisfies readonly (keyof Communication)[];

type CommunicationCopyInput = Pick<
  Communication,
  (typeof COMMUNICATION_COPY_FIELDS)[number] | 'linkedCommunicationsIds' | 'status' | 'toSend'
>;

export interface StatementCommunications {
  sent: Communication[];
  pending: Communication[];
}

const isIssuedCommunicationStatus = (status: CommunicationStatus): boolean =>
  isSentCommunicationStatus(status) || isManuallyHandledCommunicationStatus(status);

/** Communications that carry a statement and, when it was validated with them, its supporting documents. */
export const isStatementCommunication = (communication: Pick<Communication, 'statementId' | 'type'>): boolean =>
  !isNil(communication.statementId) && STATEMENT_COMMUNICATION_TYPES.includes(communication.type);

export const toStatementSupportingDocumentsPreference = (
  override?: boolean | null
): StatementSupportingDocumentsPreference => {
  if (isNil(override)) {
    return StatementSupportingDocumentsPreference.WORKSPACE_DEFAULT;
  }
  return override ? StatementSupportingDocumentsPreference.ALWAYS : StatementSupportingDocumentsPreference.NEVER;
};

/** The lease or owner override to store: null follows the workspace setting. */
export const fromStatementSupportingDocumentsPreference = (
  preference: StatementSupportingDocumentsPreference
): boolean | null => {
  switch (preference) {
    case StatementSupportingDocumentsPreference.ALWAYS:
      return true;
    case StatementSupportingDocumentsPreference.NEVER:
      return false;
    case StatementSupportingDocumentsPreference.WORKSPACE_DEFAULT:
    default:
      return null;
  }
};

/** Whether a statement send dialog ticks the supporting invoices: the lease or owner override, else the workspace. */
export const getStatementSupportingDocumentsDefault = (override?: boolean | null, settings?: Setting[]): boolean =>
  override ?? getSettingValueForBoolean(SettingType.STATEMENT_SUPPORTING_DOCUMENTS_ATTACHED, settings);

/**
 * A new communication with the content, recipients and links of a sent one, waiting for a manual send: its
 * attachments are resolved again when it is sent. Files added by hand to the sent communication are not copied.
 */
export const buildCommunicationCopyInput = (communication: Communication): CommunicationCopyInput => ({
  ...pick(communication, COMMUNICATION_COPY_FIELDS),
  linkedCommunicationsIds: [...(communication.linkedCommunicationsIds ?? []), communication.id],
  status: CommunicationStatus.TO_SEND,
  toSend: false,
});

/** The communications of a statement that already left, and those still waiting to be sent. */
export const splitStatementCommunications = (
  communications: Communication[],
  statementId: string
): StatementCommunications =>
  communications.reduce<StatementCommunications>(
    (acc, communication) => {
      if (communication.statementId !== statementId || !isStatementCommunication(communication)) {
        return acc;
      }
      const { status } = communication;
      if (isIssuedCommunicationStatus(status)) {
        acc.sent.push(communication);
      } else if (PENDING_COMMUNICATION_STATUSES.includes(status)) {
        acc.pending.push(communication);
      }
      return acc;
    },
    { sent: [], pending: [] }
  );

/**
 * A sent communication shows the documents recorded when it left; one still to send shows those of its statement.
 * A communication sent without a record, before supporting documents existed or without any, shows none.
 */
export const getSupportingDocumentsSource = (
  communication: Pick<Communication, 'statementId' | 'status' | 'supportingDocumentFileIds' | 'type'>
): SupportingDocumentsSource => {
  if (!isStatementCommunication(communication)) {
    return SupportingDocumentsSource.NONE;
  }
  if (!isNilOrEmpty(communication.supportingDocumentFileIds)) {
    return SupportingDocumentsSource.RECORDED_AT_SEND;
  }
  return isIssuedCommunicationStatus(communication.status)
    ? SupportingDocumentsSource.NONE
    : SupportingDocumentsSource.STATEMENT;
};

type StatementCharge = Pick<ChargeStatement, 'charge'>;

const getLiveCharges = (chargeStatements: StatementCharge[]): Charge[] =>
  uniqBy(
    compact(chargeStatements.map((chargeStatement) => chargeStatement.charge)).filter((charge) => !charge._deleted),
    'id'
  );

const getLiveDocument = (charge: Charge): File | undefined => {
  const { document } = charge;
  if (isNil(document) || document._deleted) {
    return;
  }
  return document;
};

/** Charges of a statement whose source document cannot be attached when the statement is sent. */
export const getChargesWithoutSupportingDocument = (chargeStatements: StatementCharge[]): Charge[] =>
  getLiveCharges(chargeStatements).filter((charge) => isNil(getLiveDocument(charge)));

/**
 * Source documents of the charges of a statement, one per file. The communication history lists whole files; the
 * send limits a document split into sections to the pages of its section.
 */
export const getSupportingDocumentFiles = (chargeStatements: StatementCharge[]): File[] => {
  const documents = getLiveCharges(chargeStatements).reduce<File[]>((acc, charge) => {
    const document = getLiveDocument(charge);
    if (!isNil(document)) {
      acc.push(document);
    }
    return acc;
  }, []);
  return uniqBy(documents, 'id');
};
